import { Authenticated, useNavigation } from "@refinedev/core";
import { Flex, Result } from "antd";
import { Button } from "@libertify/ui";
import { WarningCircleIcon, CheckCircleIcon } from "@phosphor-icons/react";
import { useAntTheme } from "hooks/useAntTheme";
import { t } from "i18next";
import { useProjectDetails } from "pages/media/projects/hooks/useProjectDetails";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { LoadingProgress } from "../../../components/LoadingProgress";
import { useProjectStatus } from "./components/useProjectStatus";
import useProgressPercentage from "../../../hooks/useProgressPercentage";
import { useProjectNavigate } from "./hooks/useProjectNavigate";
import RotatingText from "components/RotatingText/RotatingText/";
import { shuffleArray } from "utils/arrayUtils";

const STORYBOARD_DURATION = 3 * 60 * 1000;
const MEDIA_DURATION = 10 * 60 * 1000;

export const ProjectStepLoadingPage = ({
  waitingFor,
}: {
  waitingFor: string;
}) => {
  const {
    params: { projectId, storyboardId, instanceId },
    navigateToStoryboard,
  } = useProjectNavigate();
  const { data, refetch } = useProjectDetails({ projectId });
  const { isStoryboardFinal, isMediaFinal, isStoryboardError, isMediaError } =
    useProjectStatus(data?.data, storyboardId, instanceId);
  // when coming from onboarding, storyboardId is not set, so we use the first storyboard
  const storyboard = storyboardId
    ? data?.data.storyboards.find(
        (storyboard) => storyboard.id === storyboardId
      )
    : data?.data.storyboards[0];

  const { theme } = useAntTheme();
  const storyboardIsReady = isStoryboardFinal;
  const { show, create, list } = useNavigation();
  const totalDuration =
    waitingFor === "storyboard" ? STORYBOARD_DURATION : MEDIA_DURATION;

  const [progress, resetProgress] = useProgressPercentage(
    totalDuration,
    !isStoryboardFinal || !isMediaFinal,
    3000
  );

  const gotoStoryboard = useCallback(() => {
    const mediaInstanceId = instanceId ?? data?.data.media[0]?.instance_id;
    if (!projectId) return;
    navigateToStoryboard({
      projectId,
      storyboardId: storyboard?.id,
      instanceId: mediaInstanceId,
    });
  }, [
    instanceId,
    data?.data.media,
    navigateToStoryboard,
    projectId,
    storyboard?.id,
  ]);

  const goBackToProject = useCallback(() => {
    if (projectId) {
      show("media_projects", projectId);
    }
  }, [projectId, show]);

  const goToProjectsList = useCallback(() => {
    list("media_projects");
  }, [list]);

  const createNewProject = useCallback(() => {
    create("media_projects");
  }, [create]);

  // Guard against the race where the user lands here just after approving
  // settings: the backend hasn't yet flipped the storyboard from Reviewing →
  // Generating, so the resource appears "final" on mount even though work is
  // about to start. Only treat completion as real once we've observed the
  // resource in a non-final state at least once.
  const [hasObservedInProgress, setHasObservedInProgress] = useState(false);
  const currentlyInProgress =
    (waitingFor === "storyboard" && !isStoryboardFinal) ||
    (waitingFor === "media" && !isMediaFinal);
  if (currentlyInProgress && !hasObservedInProgress) {
    setHasObservedInProgress(true);
  }

  useEffect(() => {
    if (!hasObservedInProgress) return;

    if (waitingFor === "media" && isMediaFinal) {
      resetProgress(totalDuration);
      const timer = setTimeout(goBackToProject, 3000);
      return () => clearTimeout(timer);
    }

    if (waitingFor === "storyboard" && storyboardIsReady) {
      resetProgress(totalDuration);
      const timer = setTimeout(() => gotoStoryboard(), 3000);
      return () => clearTimeout(timer);
    }
  }, [
    waitingFor,
    isMediaFinal,
    storyboardIsReady,
    gotoStoryboard,
    resetProgress,
    totalDuration,
    goBackToProject,
    hasObservedInProgress,
  ]);

  const loadingMessage =
    waitingFor === "media"
      ? t("media.projects.loading.videoBeingCreated")
      : t("media.projects.loading.craftingStoryBoard");

  const rotatingTexts = useMemo(() => {
    const texts = t("media.projects.loading.rotatingTexts", {
      returnObjects: true,
    }) as string[];
    return shuffleArray(texts);
  }, []);

  const isReady =
    hasObservedInProgress &&
    ((waitingFor === "storyboard" && storyboardIsReady) ||
      (waitingFor === "media" && isMediaFinal));

  // Generation failed: the backend reports a terminal error status. This is the
  // primary "something is wrong" signal — no timer needed, an Error status is
  // terminal so there's no race to guard against.
  const isFailed =
    waitingFor === "storyboard" ? isStoryboardError : isMediaError;

  // Stall fallback: even without an error status, generation can hang. Start a
  // single timer for the expected duration; if it elapses while we're still
  // neither ready nor failed, surface a "taking longer than expected" message.
  const [hasTimedOut, setHasTimedOut] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setHasTimedOut(true), totalDuration);
    return () => clearTimeout(timer);
  }, [totalDuration]);
  const isStalled =
    hasObservedInProgress && !isReady && !isFailed && hasTimedOut;

  // Polling fallback: refetch project status every 30s in case WS notification is missed
  useEffect(() => {
    if (isReady || isFailed) return;
    const interval = setInterval(() => {
      void refetch();
    }, 30_000);
    return () => clearInterval(interval);
  }, [isReady, isFailed, refetch]);

  const backToProjectsButton = (
    <Button variant="outlined" onClick={goToProjectsList}>
      {t("media.projects.loading.backToProjects")}
    </Button>
  );

  const failedActions = (
    <Flex gap={12} justify="center">
      {backToProjectsButton}
      <Button variant="primary" onClick={createNewProject}>
        {t("media.projects.loading.createNewProject")}
      </Button>
    </Flex>
  );

  // Failed and ready both render a single centered Result, so share the wrapper.
  const resultWrapper = (result: ReactNode) => (
    <Flex
      align="center"
      justify="center"
      style={{ width: "100%", height: "100%" }}
    >
      {result}
    </Flex>
  );

  const renderContent = () => {
    if (isFailed) {
      return resultWrapper(
        <Result
          status="error"
          icon={
            <WarningCircleIcon
              size={80}
              color={theme.colorError}
              weight="fill"
            />
          }
          title={t("media.projects.loading.failed", { asset: waitingFor })}
          subTitle={t("media.projects.loading.failedDescription")}
          extra={failedActions}
        />
      );
    }

    if (isReady) {
      return resultWrapper(
        <Result
          status="success"
          icon={
            <CheckCircleIcon
              size={80}
              color={theme.colorPrimary}
              weight="fill"
            />
          }
          title={t("media.projects.loading.ready", { asset: waitingFor })}
        />
      );
    }

    return (
      <Flex
        vertical
        align="center"
        justify="center"
        style={{ height: "100%" }}
        gap={32}
      >
        <LoadingProgress message={loadingMessage} progress={progress} />

        <div
          style={{
            fontSize: "25px",
            color: theme.colorTextSecondary,
            fontWeight: 500,
            textAlign: "center",
            maxWidth: "900px",
            minHeight: "60px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0.85,
          }}
        >
          <RotatingText
            texts={rotatingTexts}
            staggerFrom="first"
            initial={{ x: "-100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "120%", opacity: 0 }}
            staggerDuration={0.015}
            rotationInterval={6000}
            transition={{
              type: "spring",
              damping: 20,
              stiffness: 200,
            }}
          />
        </div>

        {isStalled && (
          <Flex vertical align="center" gap={16} style={{ maxWidth: 600 }}>
            <div
              style={{
                fontSize: "16px",
                color: theme.colorTextSecondary,
                textAlign: "center",
              }}
            >
              {t("media.projects.loading.takingLonger")}
            </div>
            {backToProjectsButton}
          </Flex>
        )}
      </Flex>
    );
  };

  return (
    <Authenticated key="loadingProject">
      <div
        style={{
          width: "100%",
          height: "100vh",
          padding: 24,
          position: "relative",
        }}
      >
        {renderContent()}
      </div>
    </Authenticated>
  );
};
